import type { BlockInstance, TemplateModel, TemplatePagePreset, WebsitePage } from '@troia-v3/types';

/** Blocos "de moldura": ao aplicar um layout, a config já ajustada pelo usuário (menu, cores) é mantida. */
const CHROME_BLOCKS = new Set(['header', 'footer']);

export function presetsForPage(model: TemplateModel | undefined, pageId: string): TemplatePagePreset[] {
  return (model?.presets ?? []).filter((p) => p.pageId === pageId);
}

/**
 * Substitui os blocos da página pelos do preset. SEO da página fica; `instanceId`
 * é sempre novo; `config` = defaultConfig do bloco + config do preset; header/footer
 * que já existiam na página mantêm a config atual. Ordem renormalizada (header em -1).
 *
 * Depois dos blocos do preset entram, OCULTOS e no fim da lista, os `defaultBlocks`
 * da página no modelo que o preset não usa: o backend recoloca cada um deles na
 * leitura (`enrichPagesWithMissingBlocks`, sempre com `visible: false` em página
 * estática). Sem espelhar isso aqui, o painel mostraria antes de salvar uma lista
 * diferente da que volta do servidor logo depois.
 */
export function applyPreset(page: WebsitePage, preset: TemplatePagePreset, model: TemplateModel): WebsitePage {
  const existingChrome = new Map(page.blocks.filter((b) => CHROME_BLOCKS.has(b.blockId)).map((b) => [b.blockId, b]));
  let order = 0;
  const blocks: BlockInstance[] = [...preset.blocks]
    .sort((a, b) => a.order - b.order)
    .map((pb) => {
      const def = model.blocks.find((d) => d.id === pb.blockId);
      const kept = existingChrome.get(pb.blockId);
      const config = kept ? { ...kept.config } : { ...(def?.defaultConfig ?? {}), ...pb.config };
      const isHeader = pb.blockId === 'header';
      if (!isHeader) order += 1;
      return { instanceId: crypto.randomUUID(), blockId: pb.blockId, config, order: isHeader ? -1 : order, visible: pb.visible, addedByUser: false };
    });

  const inPreset = new Set(preset.blocks.map((pb) => pb.blockId));
  const modelPage = model.pages.find((p) => p.id === page.pageId);
  for (const db of modelPage?.defaultBlocks ?? []) {
    if (inPreset.has(db.blockId) || CHROME_BLOCKS.has(db.blockId)) continue;
    const def = model.blocks.find((d) => d.id === db.blockId);
    order += 1;
    blocks.push({
      instanceId: crypto.randomUUID(),
      blockId: db.blockId,
      config: { ...(def?.defaultConfig ?? {}), ...db.config },
      order,
      visible: false,
      addedByUser: false,
    });
  }

  return { ...page, blocks };
}
